import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetConsentSubmissionQuery,
  useSubmitCustomerConsentsMutation,
} from "src/graphql-next/generated/queries";
import {
  BrandCodeEnum,
  type ConsentSubmissionStatusEnum,
  type GetConsentSubmissionQuery,
} from "src/graphql-next/generated/types";
import { useCurrentAccount, useSnackbar } from "src/hooks";
import { z } from "zod";

import { FonciaConsentForm } from ".";
import { toConsentInputs } from "./consent-preferences";

export function FonciaConsentController(): ReactElement {
  const { t } = useTranslation();
  const { customer } = useCurrentAccount();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const hasValidEmail = CustomerEmailSchema.safeParse(customer.email).success;
  const { data: consentSubmission, isSuccess: isConsentSubmissionLoaded } =
    useGetConsentSubmissionQuery(undefined, {
      enabled: hasValidEmail,
      select: (data) => data.me.consentSubmission ?? null,
    });
  const [isPreviewDismissed, setIsPreviewDismissed] = useState(false);

  const notifySubmissionFailure = (): void => {
    enqueueSnackbar(
      t("Erreur lors de l'enregistrement de vos préférences de communication"),
      { variant: "error" },
    );
  };

  const { mutate: submitConsents, isPending } =
    useSubmitCustomerConsentsMutation({
      onSuccess: ({ submitCustomerConsents }) => {
        if (
          submitCustomerConsents.__typename === "InvalidConsentSubmissionError"
        ) {
          notifySubmissionFailure();
          return;
        }

        storeConsentSubmissionStatus(queryClient, {
          status: submitCustomerConsents.data.status,
        });
        enqueueSnackbar(
          t(
            "Vos préférences sont enregistrées. Elles seront actives d’ici peu.",
          ),
          { variant: "success" },
        );
      },
      onError: notifySubmissionFailure,
    });

  const hasLocalPreview =
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).get("previewConsent") ===
      "true";
  const isConsentMissing =
    hasValidEmail && isConsentSubmissionLoaded && consentSubmission === null;

  return (
    <FonciaConsentForm
      key={customer.id}
      open={hasLocalPreview ? !isPreviewDismissed : isConsentMissing}
      isSubmitting={isPending}
      firstName={customer.firstName ?? ""}
      onComplete={(preferences) => {
        if (hasLocalPreview) {
          setIsPreviewDismissed(true);
          return;
        }
        submitConsents({
          brandCode: BrandCodeEnum.Foncia,
          consents: toConsentInputs(preferences),
        });
      }}
    />
  );
}

function storeConsentSubmissionStatus(
  queryClient: QueryClient,
  { status }: { status: ConsentSubmissionStatusEnum },
): void {
  queryClient.setQueryData<GetConsentSubmissionQuery>(
    useGetConsentSubmissionQuery.getKey(),
    (cachedData) =>
      cachedData && {
        ...cachedData,
        me: {
          ...cachedData.me,
          consentSubmission: { __typename: "ConsentSubmission", status },
        },
      },
  );
}

const CustomerEmailSchema = z.email();
